/**
 * LLM Redactions tab – shows the same section structure as the Document Editor (protocol sections).
 * For each section we:
 *  - Fetch the latest content from the Document Editor (same API as the editor)
 *  - Display the plain-text output
 *  - Provide a per-section prompt box + Generate button shell (behaviour to be wired later)
 */
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Chip,
  TextField,
  Button,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DocumentCanvas from '../documentEditor/DocumentCanvas';
import { pdf } from '@react-pdf/renderer';
import ProtocolPdfDocument from '../documentEditor/ProtocolPdfDocument';
import api from '../../services/api';
import documentEditorService from '../../services/documentEditor.service';

const stripHtmlToText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
};

const LlmRedactionsTab = ({ projectId }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [promptById, setPromptById] = useState({});
  const [redactedOutputById, setRedactedOutputById] = useState({});
  const [statusById, setStatusById] = useState({});
  const [mode, setMode] = useState('edit'); // 'edit' | 'canvas'
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null);

  useEffect(() => {
    if (!projectId) {
      setSections([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    documentEditorService
      .getAllSections(projectId)
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.documents || data?.sections || []);
        setSections(list);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load sections');
        setSections([]);
      })
      .finally(() => setLoading(false));
  }, [projectId]);

  // When sections change, initialise local prompts from the section.prompt field (like DocumentEditor)
  useEffect(() => {
    const map = {};
    sections.forEach((s) => {
      if (s && (s._id || s.id)) {
        map[s._id || s.id] = s.prompt || '';
      }
    });
    setPromptById(map);
  }, [sections]);

  // Load any previously saved LLM redactions for this project
  useEffect(() => {
    if (!projectId || sections.length === 0) return;
    api
      .get(`/export/llm-protocol-redactions/${projectId}`)
      .then((res) => {
        if (!res.data?.success || !Array.isArray(res.data.sections)) return;
        const byId = {};
        const prompts = {};
        res.data.sections.forEach((s) => {
          if (!s.sectionId) return;
          if (s.redactedOutput) byId[s.sectionId] = s.redactedOutput;
          if (s.lastPrompt) prompts[s.sectionId] = s.lastPrompt;
        });
        if (Object.keys(byId).length) {
          setRedactedOutputById((prev) => ({ ...prev, ...byId }));
        }
        if (Object.keys(prompts).length) {
          setPromptById((prev) => ({ ...prev, ...prompts }));
        }
      })
      .catch(() => {});
  }, [projectId, sections]);

  const handlePromptChange = (id, value) => {
    setPromptById((prev) => ({ ...prev, [id]: value }));
  };

  // When user clicks Generate for a section:
  // Replace the FULL sentence the user typed (case-insensitive) within this
  // section's HTML content with the word \"[redacted]\" for each token.
  // Matching is tolerant to extra whitespace and inline HTML tags between words,
  // so paragraphs / long sentences are handled more robustly.
  const handleGenerateClick = (sectionId) => {
    const prompt = promptById[sectionId] || '';
    const term = prompt.trim();
    if (!term) {
      setStatusById((prev) => ({ ...prev, [sectionId]: 'Enter some text to redact.' }));
      return;
    }
    const section = sections.find(
      (s) => (s._id || s.id) === sectionId,
    );
    if (!section) {
      setStatusById((prev) => ({ ...prev, [sectionId]: 'Section not found.' }));
      return;
    }
    const originalHtml = section.output || '';
    if (!originalHtml) {
      setStatusById((prev) => ({ ...prev, [sectionId]: 'This section has no content yet.' }));
      return;
    }

    // Build a regex that allows arbitrary whitespace OR HTML tags between words.
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const flexiblePattern = escaped.replace(/\s+/g, '(?:\\s|<[^>]*>)+');
    const re = new RegExp(flexiblePattern, 'gi');

    let totalTokens = 0;
    const redactedHtml = originalHtml.replace(re, (match) => {
      // Strip tags to count how many tokens are in the matched sentence
      const plain = match.replace(/<[^>]*>/g, ' ');
      const tokens = plain.split(/\s+/).filter(Boolean);
      const count = tokens.length || 1;
      totalTokens += count;
      // Replace the whole sentence with one [redacted] per token
      return new Array(count).fill('[redacted]').join(' ');
    });

    if (!totalTokens) {
      setStatusById((prev) => ({ ...prev, [sectionId]: 'No matching text found in this section.' }));
      return;
    }

    setRedactedOutputById((prev) => ({ ...prev, [sectionId]: redactedHtml }));
    setStatusById((prev) => ({ ...prev, [sectionId]: `Replaced ${totalTokens} word(s).` }));
  };

  const handleExportRedactedPdf = async () => {
    if (!projectId || sections.length === 0) return;
    setExporting(true);
    setExportError(null);
    try {
      const exportSections = sections.map((s) => {
        const id = s._id || s.id;
        const html = redactedOutputById[id] ?? s.output ?? '';
        return {
          ...s,
          output: html || '',
        };
      });

      // Use the same React-PDF document as the native export so formatting matches.
      const blob = await pdf(
        <ProtocolPdfDocument
          sections={exportSections}
          fontConfig={null}
          soaData={null}
          trialSchemaImageUrl={null}
        />,
      ).toBlob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectId}_llm_redacted_protocol.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || 'Failed to export redacted PDF');
    } finally {
      setExporting(false);
    }
  };

  // Build payload: sections that have redacted output OR a prompt (so prompts persist too)
  const buildSectionsPayload = () => {
    const sectionIds = new Set([
      ...Object.keys(redactedOutputById).filter((id) => redactedOutputById[id] && String(redactedOutputById[id]).trim()),
      ...Object.keys(promptById).filter((id) => promptById[id] && String(promptById[id]).trim()),
    ]);
    return Array.from(sectionIds).map((sectionId) => {
      const section = sections.find((s) => (s._id || s.id) === sectionId) || {};
      return {
        sectionId,
        title: section.title || '',
        redactedOutput: redactedOutputById[sectionId] || '',
        lastPrompt: promptById[sectionId] || '',
      };
    });
  };

  const saveRedactionsToServer = async (options = {}) => {
    const { showStatus = true } = options;
    if (!projectId || sections.length === 0) return;
    const sectionsPayload = buildSectionsPayload();
    if (sectionsPayload.length === 0) return;
    if (showStatus) setSaveStatus('saving');
    try {
      await api.put(`/export/llm-protocol-redactions/${projectId}`, {
        sections: sectionsPayload,
      });
      if (showStatus) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(null), 2500);
      }
    } catch (e) {
      if (showStatus) {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 4000);
      }
      console.error('Failed to save LLM redactions', e);
    }
  };

  // Persist LLM redactions (debounced). Never send empty array – that would wipe DB on load.
  useEffect(() => {
    if (!projectId || sections.length === 0) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const sectionsPayload = buildSectionsPayload();
      if (sectionsPayload.length === 0) return;
      saveRedactionsToServer({ showStatus: false }).catch(() => {});
    }, 800);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [projectId, redactedOutputById, promptById, sections]);

  if (!projectId) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">Select a study / project to load document editor sections.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <CircularProgress />
        <Typography>Loading sections…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        LLM Redactions – Document sections
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Same structure as the Protocol tabs in the Document Editor.
        Switch between <strong>Edit</strong> (plain text + prompt) and <strong>Canvas</strong> (document-style layout) views.
      </Typography>

      {/* Mode toggle */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          size="small"
          variant={mode === 'edit' ? 'contained' : 'outlined'}
          onClick={() => setMode('edit')}
        >
          Edit mode
        </Button>
        <Button
          size="small"
          variant={mode === 'canvas' ? 'contained' : 'outlined'}
          onClick={() => setMode('canvas')}
        >
          Canvas mode
        </Button>
        <Box sx={{ flex: 1 }} />
        {saveStatus === 'saving' && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Saving…
          </Typography>
        )}
        {saveStatus === 'saved' && (
          <Typography variant="caption" color="success.main" sx={{ alignSelf: 'center' }}>
            Saved
          </Typography>
        )}
        {saveStatus === 'error' && (
          <Typography variant="caption" color="error.main" sx={{ alignSelf: 'center' }}>
            Save failed
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={saveRedactionsToServer}
          disabled={sections.length === 0 || saveStatus === 'saving' || buildSectionsPayload().length === 0}
        >
          Save redactions
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={handleExportRedactedPdf}
          disabled={exporting || sections.length === 0}
        >
          {exporting ? 'Exporting…' : 'Export redacted PDF'}
        </Button>
      </Stack>

      {sections.length === 0 ? (
        <Alert severity="info">No sections found. Create protocol sections in the Document Editor first.</Alert>
      ) : (
        <>
          {exportError && (
            <Alert severity="error" sx={{ mb: 1 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}
          {mode === 'edit' ? (
            <Stack direction="column" spacing={1.5}>
              {sections.map((section) => (
                <Card
                  key={section._id || section.id || section.title}
                  variant="outlined"
                  sx={{ width: '100%' }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <DescriptionOutlinedIcon color="action" fontSize="small" />
                      <Typography variant="subtitle2" noWrap>
                        {section.title || 'Untitled'}
                      </Typography>
                    </Stack>
                    {section.documentType && (
                      <Chip label={section.documentType} size="small" sx={{ mt: 0.5 }} variant="outlined" />
                    )}

                    {/* Content preview – original vs redacted */}
                    {(() => {
                      const id = section._id || section.id;
                      const originalHtml = section.output || '';
                      const redactedHtml = redactedOutputById[id];

                      // If we have a redacted version, show both original and redacted
                      if (redactedHtml && redactedHtml.trim()) {
                        return (
                          <Box sx={{ mt: 1, mb: 1 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                  Original content
                                </Typography>
                                <Box
                                  sx={{
                                    maxHeight: 140,
                                    overflow: 'auto',
                                    p: 1,
                                    bgcolor: '#fafafa',
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {originalHtml && originalHtml.trim()
                                    ? stripHtmlToText(originalHtml)
                                    : (
                                      <Typography variant="caption" color="text.disabled">
                                        No content generated yet.
                                      </Typography>
                                    )}
                                </Box>
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                  Redacted content
                                </Typography>
                                <Box
                                  sx={{
                                    maxHeight: 140,
                                    overflow: 'auto',
                                    p: 1,
                                    bgcolor: '#fafafa',
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {stripHtmlToText(redactedHtml)}
                                </Box>
                              </Box>
                            </Stack>
                          </Box>
                        );
                      }

                      // Before any redaction, show only the original content
                      return (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                            Latest content
                          </Typography>
                          <Box
                            sx={{
                              maxHeight: 140,
                              overflow: 'auto',
                              p: 1,
                              bgcolor: '#fafafa',
                              borderRadius: 1,
                              border: '1px solid #eee',
                              fontSize: 12,
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {originalHtml && originalHtml.trim()
                              ? stripHtmlToText(originalHtml)
                              : (
                                <Typography variant="caption" color="text.disabled">
                                  No content generated yet.
                                </Typography>
                              )}
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* Per-section prompt (local only for now) */}
                    <TextField
                      label="Prompt for this section"
                      multiline
                      minRows={2}
                      fullWidth
                      size="small"
                      value={promptById[section._id || section.id] ?? ''}
                      onChange={(e) => handlePromptChange(section._id || section.id, e.target.value)}
                      sx={{ mt: 0.5, mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleGenerateClick(section._id || section.id)}
                        disabled={!projectId}
                      >
                        Generate content
                      </Button>
                    </Box>
                    {statusById[section._id || section.id] && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {statusById[section._id || section.id]}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Box sx={{ mt: 1 }}>
              {Object.keys(redactedOutputById).some((k) => redactedOutputById[k] && redactedOutputById[k].trim()) ? (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxHeight: '70vh',
                      overflow: 'auto',
                      border: '1px solid #eee',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Original document
                    </Typography>
                    <DocumentCanvas
                      sections={sections}
                      mode="preview"
                      actualProjectId={projectId}
                      trialSchemaImageUrl={null}
                      generatedSoAData={null}
                      handleSoADataUpdate={null}
                      handleRegenerateSoA={null}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxHeight: '70vh',
                      overflow: 'auto',
                      border: '1px solid #eee',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: '#fafafa',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Redacted document
                    </Typography>
                    <DocumentCanvas
                      sections={sections.map((s) => {
                        const id = s._id || s.id;
                        const html = redactedOutputById[id];
                        return html
                          ? { ...s, output: html }
                          : s;
                      })}
                      mode="preview"
                      actualProjectId={projectId}
                      trialSchemaImageUrl={null}
                      generatedSoAData={null}
                      handleSoADataUpdate={null}
                      handleRegenerateSoA={null}
                    />
                  </Box>
                </Stack>
              ) : (
                <Box
                  sx={{
                    maxHeight: '70vh',
                    overflow: 'auto',
                    border: '1px solid #eee',
                    borderRadius: 1,
                    p: 1,
                    bgcolor: '#fafafa',
                  }}
                >
                  <DocumentCanvas
                    sections={sections}
                    mode="preview"
                    actualProjectId={projectId}
                    trialSchemaImageUrl={null}
                    generatedSoAData={null}
                    handleSoADataUpdate={null}
                    handleRegenerateSoA={null}
                  />
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default LlmRedactionsTab;
